/**
 * @engine/vfx - Output Context Nodes
 *
 * Nodes for rendering particles.
 */

import type { VFXNodeDefinition } from '../types';
import { dataPort, flowPort } from '../types';

/**
 * Output context node definitions.
 */
export const OUTPUT_NODES: readonly VFXNodeDefinition[] = [
  // ---- Render Unlit Quad ----
  {
    id: 'vfx.output.quad',
    name: 'Render Unlit Quad',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as camera-facing quads',
    color: '#9C27B0',
    renderType: 'quad',
    inputs: [
      flowPort('exec', ''),
      dataPort('texture', 'Texture', 'texture2d'), // Uses default_texture.png when not connected
      dataPort('blendMode', 'Blend', 'int', {
        defaultValue: 0,
        options: ['Alpha', 'Additive', 'Multiply', 'Premultiplied'],
      }),
      dataPort('orientMode', 'Orient', 'int', {
        defaultValue: 1,
        options: ['Face Camera Position', 'Face Camera Plane'],
      }),
      dataPort('softParticle', 'Soft Particle', 'bool', { defaultValue: true }),
      dataPort('stretchMode', 'Stretch', 'int', {
        defaultValue: 0,
        options: ['None', 'Velocity', 'Custom'],
      }),
      dataPort('stretchScale', 'Stretch Scale', 'float', { defaultValue: 1.0, range: { min: 0, max: 10 } }),
    ],
    outputs: [],
    modifiesParticle: false,
    // Rendering is generated from renderShaders templates via RenderOptions —
    // output nodes carry configuration, not shader code.
  },

  // ---- Render Unlit Sprite ----
  {
    id: 'vfx.output.sprite',
    name: 'Render Unlit Sprite',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles with flipbook animation',
    color: '#9C27B0',
    renderType: 'sprite',
    inputs: [
      flowPort('exec', ''),
      dataPort('texture', 'Texture', 'texture2d'), // Uses default_texture.png when not connected
      dataPort('columns', 'Columns', 'int', { defaultValue: 4, range: { min: 1, max: 32 } }),
      dataPort('rows', 'Rows', 'int', { defaultValue: 4, range: { min: 1, max: 32 } }),
      dataPort('blendMode', 'Blend', 'int', {
        defaultValue: 0,
        options: ['Alpha', 'Additive', 'Multiply', 'Premultiplied'],
      }),
      dataPort('softParticle', 'Soft Particle', 'bool', { defaultValue: false }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Render Unlit Mesh ----
  {
    id: 'vfx.output.mesh',
    name: 'Render Unlit Mesh',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as 3D meshes',
    color: '#9C27B0',
    inputs: [
      flowPort('exec', ''),
      dataPort('mesh', 'Mesh', 'int', {
        defaultValue: 0,
        options: ['Quad', 'Triangle', 'Hexagon', 'Custom'],
      }),
      dataPort('texture', 'Texture', 'texture2d'),
      dataPort('faceCamera', 'Face Camera', 'bool', { defaultValue: true }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Render Unlit Trail ----
  {
    id: 'vfx.output.trail',
    name: 'Render Unlit Trail',
    category: 'output',
    contexts: ['output'],
    description: 'Render particle trails',
    color: '#9C27B0',
    inputs: [
      flowPort('exec', ''),
      dataPort('width', 'Width', 'float', { defaultValue: 0.1, range: { min: 0, max: 10 } }),
      dataPort('segments', 'Segments', 'int', { defaultValue: 16, range: { min: 2, max: 128 } }),
      dataPort('texture', 'Texture', 'texture2d'),
      dataPort('widthCurve', 'Width Curve', 'curve'),
      dataPort('colorGradient', 'Color', 'gradient'),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Render Unlit Point ----
  {
    id: 'vfx.output.point',
    name: 'Render Unlit Point',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as points (GPU point sprites)',
    color: '#9C27B0',
    renderType: 'point',
    inputs: [
      flowPort('exec', ''),
      dataPort('size', 'Size', 'float', { defaultValue: 4, range: { min: 1, max: 64 } }),
      dataPort('blendMode', 'Blend', 'int', {
        defaultValue: 1,
        options: ['Alpha', 'Additive', 'Multiply'],
      }),
      dataPort('softParticle', 'Soft Particle', 'bool', { defaultValue: true }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Render Unlit Line ----
  {
    id: 'vfx.output.line',
    name: 'Render Unlit Line',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as velocity-stretched quads',
    color: '#9C27B0',
    renderType: 'line',
    inputs: [
      flowPort('exec', ''),
      dataPort('length', 'Length', 'float', { defaultValue: 1.0, range: { min: 0.1, max: 20 } }),
      dataPort('width', 'Width', 'float', { defaultValue: 0.5, range: { min: 0.01, max: 5 } }),
      dataPort('velocityAlign', 'Velocity Align', 'bool', { defaultValue: true }),
      dataPort('blendMode', 'Blend', 'int', {
        defaultValue: 1,
        options: ['Alpha', 'Additive', 'Multiply'],
      }),
      dataPort('softParticle', 'Soft Particle', 'bool', { defaultValue: true }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Distortion ----
  {
    id: 'vfx.output.distortion',
    name: 'Distortion',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as distortion effects',
    color: '#9C27B0',
    inputs: [
      flowPort('exec', ''),
      dataPort('normalMap', 'Normal Map', 'texture2d', { required: true }),
      dataPort('strength', 'Strength', 'float', { defaultValue: 0.1, range: { min: 0, max: 1 } }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Render Lit Quad ----
  {
    id: 'vfx.output.lit_quad',
    name: 'Render Lit Quad',
    category: 'output',
    contexts: ['output'],
    description: 'Render particles as camera-facing quads with Forward+ lighting',
    color: '#9C27B0',
    renderType: 'quad',
    inputs: [
      flowPort('exec', ''),
      dataPort('texture', 'Texture', 'texture2d'),
      dataPort('blendMode', 'Blend', 'int', {
        defaultValue: 0,
        options: ['Alpha', 'Additive', 'Multiply', 'Premultiplied'],
      }),
      dataPort('orientMode', 'Orient', 'int', {
        defaultValue: 1,
        options: ['Face Camera Position', 'Face Camera Plane'],
      }),
      dataPort('softParticle', 'Soft Particle', 'bool', { defaultValue: true }),
      dataPort('stretchMode', 'Stretch', 'int', {
        defaultValue: 0,
        options: ['None', 'Velocity', 'Custom'],
      }),
      dataPort('stretchScale', 'Stretch Scale', 'float', { defaultValue: 1.0, range: { min: 0, max: 10 } }),
    ],
    outputs: [],
    modifiesParticle: false,
  },

  // ---- Light Source ----
  {
    id: 'vfx.output.light',
    name: 'Light Source',
    category: 'output',
    contexts: ['output'],
    description: 'Each alive particle emits a point light',
    color: '#FFB300',
    inputs: [
      flowPort('exec', ''),
      dataPort('radius', 'Radius', 'float', { defaultValue: 150, range: { min: 1, max: 2000 } }),
      dataPort('intensity', 'Intensity', 'float', { defaultValue: 1.0, range: { min: 0, max: 10 } }),
      dataPort('falloff', 'Falloff', 'float', { defaultValue: 2.0, range: { min: 0.1, max: 10 } }),
      dataPort('colorMode', 'Color Mode', 'int', {
        defaultValue: 0,
        options: ['Particle Color', 'Override'],
      }),
      dataPort('overrideColor', 'Override Color', 'color', { defaultValue: { r: 1, g: 1, b: 1, a: 1 } }),
      dataPort('intensityFromAlpha', 'Intensity × Alpha', 'bool', { defaultValue: true }),
      dataPort('renderQuad', 'Render Quad', 'bool', { defaultValue: false }),
    ],
    outputs: [],
    modifiesParticle: false,
  },
];
